import sentinelToEmpty from '../sentinelValue'

/** De entiteiten waarvan we statuswissels bijhouden. */
const STATUS_HISTORY_ENTITY_TYPES = ['adress', 'persona']

/**
 * @typedef {object} StatusHistoryEntry
 * @property {string} ID - Eigen id van de regel
 * @property {string} CompagnyID - Tenant
 * @property {string} Compagny - Tenant (zelfde waarde, bestaande vorm)
 * @property {'adress'|'persona'} EntityType - Huishouden of deelnemer
 * @property {string} EntityID - Adresses.ID of Personas.ID (niet de docid)
 * @property {string} FromStatus - Status ervoor; leeg bij de eerste status
 * @property {string} ToStatus - Status erna
 * @property {string} ChangedBy - Gebruiker die de wissel deed
 * @property {*} ChangedAt - Tijdstip van de wissel
 * @property {boolean} Deleted - Soft delete (ADR-008)
 * @property {string} DeletedAt - Soft delete
 * @property {string} DeletedBy - Soft delete
 */

/**
 * Bouwt één regel statushistorie. Puur — de aanroeper schrijft 'm weg.
 *
 * Geeft `null` terug wanneer er niets te loggen valt: dezelfde status, een
 * ontbrekend id, of een onbekend soort entiteit. Zo hoeft geen enkele
 * aanroeper die controles zelf te herhalen.
 *
 * @param {object} params
 * @param {string} params.client - Tenant CompagnyID
 * @param {'adress'|'persona'} params.entityType
 * @param {string} params.entityID
 * @param {string} [params.fromStatus] - Leeg/afwezig bij aanmaken
 * @param {string} params.toStatus
 * @param {string} [params.changedBy]
 * @param {*} params.changedAt - Firestore-tijdstempel of ms
 * @param {() => string} params.newId - Levert het id van de regel
 * @returns {StatusHistoryEntry|null}
 */
const buildStatusHistoryEntry = ({
    client,
    entityType,
    entityID,
    fromStatus,
    toStatus,
    changedBy,
    changedAt,
    newId,
}) => {
    if (!client) return null
    if (!STATUS_HISTORY_ENTITY_TYPES.includes(entityType)) return null

    // '' TELT HIER ALS SENTINEL: zonder id is er geen regel om te schrijven, en
    // '' stond al in de eigen lijst. `sentinelToEmpty` coerceert net als het
    // oude `String(entityID ?? '')`, dus een id dat ooit als getal is
    // weggeschreven blijft geldig.
    // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
    const id = sentinelToEmpty(entityID)
    if (!id) return null

    const to = String(toStatus ?? '').trim()
    if (!to) return null

    const from = String(fromStatus ?? '').trim()
    if (from === to) return null

    return {
        ID: newId(),
        CompagnyID: client,
        Compagny: client,
        EntityType: entityType,
        EntityID: id,
        FromStatus: from,
        ToStatus: to,
        ChangedBy: changedBy || '',
        ChangedAt: changedAt,
        Deleted: false,
        DeletedAt: '',
        DeletedBy: '',
    }
}

export default buildStatusHistoryEntry
export { STATUS_HISTORY_ENTITY_TYPES }
